/**
 * Marks a conversation's unread notifications as read once its thread is open,
 * so the bell list stops advertising messages the member has already read.
 * The API has no read-by-conversation endpoint, so the rows are resolved from
 * the list first and then marked one by one.
 */
import { useEffect, useRef } from 'react';
import notificationApiService from '@wlb/shared/api/services/notificationApiService';
import { getTimezoneOffsetMinutes } from '@wlb/shared/utils/dateHelpers';
import { extractNotificationRows, NOTIFICATIONS_PAGE_SIZE } from './notificationRows';

export function useConversationNotificationsRead(
  conversationId?: string | null,
  enabled = true,
): void {
  const handledRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !conversationId) return undefined;
    // One sweep per conversation per mount — reopening the same thread in the
    // same session has nothing left to clear.
    if (handledRef.current.has(conversationId)) return undefined;
    const handled = handledRef.current;
    handled.add(conversationId);

    let cancelled = false;
    let swept = false;

    const clear = async (): Promise<void> => {
      const response = await notificationApiService.getNotifications({
        startIndex: 1,
        pageSize: NOTIFICATIONS_PAGE_SIZE,
        timezoneOffset: getTimezoneOffsetMinutes(),
      });
      if (cancelled) return;

      const ids = extractNotificationRows(response)
        .filter(
          row =>
            !row.isRead &&
            typeof row.notificationId === 'number' &&
            row.conversationId != null &&
            String(row.conversationId) === conversationId,
        )
        .map(row => row.notificationId as number);

      await Promise.all(
        ids.map(id => notificationApiService.updateReadStatus({ notificationId: id, isRead: true })),
      );
      swept = true;
    };

    // Background hygiene — a failure only leaves the rows unread, so it is
    // retried on the next visit instead of interrupting the chat.
    clear().catch(() => {
      handled.delete(conversationId);
    });

    return () => {
      cancelled = true;
      // Switching threads mid-request abandons the sweep before it marks
      // anything, so release the guard and let the next visit try again.
      if (!swept) handled.delete(conversationId);
    };
  }, [conversationId, enabled]);
}
